import { Base64 } from 'js-base64';
import pako from 'pako';
import { z } from 'zod';

import { assertUnreachable } from 'ms-utils/typescript-utils';

import {
  teacherClassSkillsPath,
  teacherClassSkillsUrl,
  type TeacherClassUrlParams,
} from './class';

// Deprecated Waypoints URLs that we still want to support in
// case it is boorkmarked or linked from an email/article.
export const deprecatedWayspacePath = '/teacher/waypoints/:classId';
export const deprecatedClasslessWayspacePath = '/teacher/waypoints';

export const classlessWayspacePath = '/teacher/skills';

// Overview
type ClassSkillsOverviewUrlParams = TeacherClassUrlParams & {
  strandId?: string | undefined | null;
  gradeId?: string | undefined | null;
};
export const getClassSkillsOverviewBaseUrl = ({
  classId,
}: TeacherClassUrlParams) => `${teacherClassSkillsUrl({ classId })}/overview`;
export const getClassSkillsOverviewUrl = ({
  classId,
  strandId,
}: ClassSkillsOverviewUrlParams) =>
  `${teacherClassSkillsUrl({ classId })}/overview/strand/${
    strandId != null ? `${strandId}/` : 'all'
  }`;
export const classSkillsOverviewPath = `${teacherClassSkillsPath}/overview`;
export const classSkillsOverviewAllPath = `${classSkillsOverviewPath}/strand/all`;
export const classSkillsOverviewStrandPath = `${classSkillsOverviewPath}/strand/:strandId`;

// Growth
type ClassSkillsGrowthUrlParams = TeacherClassUrlParams & {
  strand?: string | undefined | null;
};
export const getClassSkillsGrowthBaseUrl = ({
  classId,
}: TeacherClassUrlParams) => `${teacherClassSkillsUrl({ classId })}/growth`;
export const getClassSkillsGrowthUrl = ({
  classId,
  strand,
}: ClassSkillsGrowthUrlParams) =>
  `${teacherClassSkillsUrl({ classId })}/growth/strand/${
    strand ? `${strand}` : 'all'
  }`;
export const classSkillsGrowthPath = `${teacherClassSkillsPath}/growth`;
export const classSkillsGrowthAllPath = `${teacherClassSkillsPath}/growth/strand/all`;
export const classSkillsGrowthStrandPath = `${classSkillsGrowthPath}/strand/:strandId`;

// Activity
type ClassSkillsActivityUrlParams = TeacherClassUrlParams;
export const getClassSkillsActivityUrl = ({
  classId,
}: ClassSkillsActivityUrlParams) =>
  `${teacherClassSkillsUrl({ classId })}/activity`;
export const classSkillsActivityPath = `${teacherClassSkillsPath}/activity`;

// Curriculum
type ClassSkillsCurriculumUrlParams = TeacherClassUrlParams & {
  strandIds: string[];
  gradeIds: string[];
  substrandIds: string[];
};
export const getClassSkillsCurriculumBaseUrl = ({
  classId,
}: TeacherClassUrlParams) => `${teacherClassSkillsUrl({ classId })}/curriculum`;
export const getClassSkillsCurriculumUrl = ({
  classId,
  gradeIds,
  strandIds,
  substrandIds,
}: ClassSkillsCurriculumUrlParams) => {
  const compressedSubstrandIds = compressSubstrandIdsParam(
    substrandIds.length === 0
      ? null
      : substrandIds.reduce<Record<string, boolean>>(
          (acc, id) => ({ ...acc, [id]: true }),
          {},
        ),
  );
  return `${teacherClassSkillsUrl({ classId })}/curriculum/grade/${
    gradeIds.length === 0 ? 'all' : gradeIds.join(',')
  }/strand/${strandIds.length === 0 ? 'all' : strandIds.join(',')}/substrands/${
    compressedSubstrandIds ?? 'all'
  }`;
};
export const classSkillsCurriculumPath = `${teacherClassSkillsPath}/curriculum`;
// Renaming the param names to gradeIds and strandIds as we're now allowing multiple selections of them too, but not changing the prefixes in the path not to break existing links, the values are "all" or comma separated strings for gradeIds and strandIds, and "all" or encoded strings for substrandIds
export const classSkillsCurriculumAllPaths = [
  '/teacher/classes/:classId/skills/curriculum/grade/:gradeIds/strand/:strandIds/substrands/:substrandIds',
] as const;

export function literalAllOrCommaSeparatedIdsToArrayOfIds(
  literalAllOrCommaSeparatedIds: string,
) {
  return literalAllOrCommaSeparatedIds === 'all'
    ? []
    : literalAllOrCommaSeparatedIds.split(',').filter(id => id.trim() !== '');
}

// To avoid ridiculously long URLS, we compress the selected substrand ids.
// This pattern should be useful more broadly in the teacher reports, since
// there are already very unwieldy URLs being used.
// The functions are inlcuded here because they are only being used for this view for now.

export function compressSubstrandIdsParam(
  selectedSubstrandIds: Record<string, boolean> | null,
): string | undefined {
  if (selectedSubstrandIds === null) {
    return undefined;
  }
  return Base64.fromUint8Array(
    pako.deflate(JSON.stringify(selectedSubstrandIds)),
    true,
  );
}

export function decompressSubstrandIdsParam(
  compressedSubstrandIds: string | undefined,
): Record<string, boolean> | null | undefined {
  if (compressedSubstrandIds === undefined) {
    return null;
  }
  try {
    const parsedObject = JSON.parse(
      pako.inflate(Base64.toUint8Array(compressedSubstrandIds), {
        to: 'string',
      }),
    );
    // zod throws if the object is not a record of string to boolean
    z.record(z.string(), z.boolean()).parse(parsedObject);
    return parsedObject;
  } catch {
    return undefined;
  }
}

export function substrandsMapToArrayOfIds(
  substrandsMap: Record<string, boolean> | null | undefined,
): string[] {
  if (substrandsMap == null) return [];
  return Object.entries(substrandsMap)
    .filter(([_, isIncluded]) => isIncluded)
    .map(([id]) => id);
}

export function literalAllOrCompressedSubstrandIdsToArrayOfIds(
  literalAllOrCompressedSubstrandIds: string,
) {
  if (literalAllOrCompressedSubstrandIds === 'all') return [];

  const decompressedSubstrandIds = decompressSubstrandIdsParam(
    literalAllOrCompressedSubstrandIds,
  );
  return substrandsMapToArrayOfIds(decompressedSubstrandIds);
}

export type ClassSkillsView = 'overview' | 'growth' | 'activity' | 'curriculum';
type GetClassSkillsUrlFunction = (p: ClassSkillsOverviewUrlParams) => string;

export const classSkillsViewPath = `${teacherClassSkillsPath}/:view`;
export const classSkillsViewStrandPath = `${teacherClassSkillsPath}/:view/strand/:strandId`;
export const classSkillsViewGradeStrandPath = `${teacherClassSkillsPath}/:view/grade/:gradeId/strand/:strandId`;

type MakeClassSkillsUrlParams = {
  view?: ClassSkillsView | undefined;
};

export const makeClassSkillsUrl = ({
  view,
}: MakeClassSkillsUrlParams): GetClassSkillsUrlFunction => {
  if (view == null) return ({ classId }) => teacherClassSkillsUrl({ classId });
  switch (view) {
    case 'overview':
      return ({ classId, strandId }) =>
        getClassSkillsOverviewUrl({ classId, strandId });
    case 'growth':
      return ({ classId, strandId }) =>
        getClassSkillsGrowthUrl({ classId, strand: strandId });
    case 'activity':
      return ({ classId }) => getClassSkillsActivityUrl({ classId });
    case 'curriculum':
      return ({ classId, strandId, gradeId }) =>
        getClassSkillsCurriculumUrl({
          classId,
          strandIds: strandId ? [strandId] : [],
          gradeIds: gradeId ? [gradeId] : [],
          substrandIds: [],
        });
    default: {
      assertUnreachable(view, `Incorrect view for Class skills: ${view}`);
    }
  }
};

export const makeClassSkillsBaseUrl = ({
  view,
}: MakeClassSkillsUrlParams): GetClassSkillsUrlFunction => {
  if (view == null) return ({ classId }) => teacherClassSkillsUrl({ classId });
  switch (view) {
    case 'overview':
      return ({ classId }) => getClassSkillsOverviewBaseUrl({ classId });
    case 'growth':
      return ({ classId }) => getClassSkillsGrowthBaseUrl({ classId });
    case 'activity':
      return ({ classId }) => getClassSkillsActivityUrl({ classId });
    case 'curriculum':
      return ({ classId }) => getClassSkillsCurriculumBaseUrl({ classId });
    default: {
      assertUnreachable(view, `Incorrect view for Class skills: ${view}`);
    }
  }
};
